<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Config } from '@/Config'
import DateRange from '@/components/DateRange.vue'
import EventTracksBadge from '@/components/EventTracksBadge.vue'
import UserName from '@/components/UserName.vue'
import { eventStateColor } from '@/lib/events'
import { eventTypeStyle } from '@/lib/eventTypes'
import { podiumClass, podiumColor } from '@/lib/podium'
import { useEventTitle } from '@/composables/useEventTitle'

/**
 * The season on the events page: the wide card on the left of the open events, with the top of the
 * general standings under the header.
 *
 * It only renders the event it is given: the list, the request and the errors stay with the page
 * (see EventListView). The standings arrive with the list (EventLib::present), composed from the
 * cached event blob: the first CARD_STANDINGS_LIMIT players, plus the logged-in player's own row —
 * flagged with `isMe` — when they sit further down.
 */
const props = defineProps({
  /** Open season event, in the shape returned by GET /api/events. */
  event: { type: Object, required: true },
})

const { t } = useI18n()
const eventTitle = useEventTitle()

const style = computed(() => eventTypeStyle(props.event.eventType))

const headerStyle = computed(() => ({
  background: `rgba(var(--v-theme-${style.value.color}), 0.12)`,
}))

const eventLink = computed(() => ({
  name: 'event_detail',
  params: { id: props.event.id },
}))

/** The top of the general standings: the card shows a leaderboard, not the whole season. */
const topRows = computed(() => (props.event.standings ?? []).slice(0, Config.events.standingsLimit))

/** Row of the logged-in player when it falls past the top ten: it stays pinned under the table. */
const myRowOutsideTop = computed(() => (props.event.standings ?? [])
  .slice(Config.events.standingsLimit)
  .find(row => row.isMe) ?? null)

/** The player's own result: the points of a points event, the time otherwise. */
const myResult = computed(() => {
  const entry = props.event.myEntry
  if (!entry) return ''

  return props.event.pointsBased && entry.points !== null ? entry.points : (entry.resultTime ?? '')
})

const hasPoints = computed(() => props.event.pointsBased && props.event.myEntry?.points !== null)
</script>

<template>
  <v-card elevation="3" rounded="lg" class="h-100 d-flex flex-column overflow-hidden">
    <RouterLink
      v-ripple
      :to="eventLink"
      class="season-head d-flex align-center ga-3 ga-sm-4 pa-4 pa-sm-5 text-decoration-none text-high-emphasis"
      :style="headerStyle"
    >
      <v-avatar :color="style.color" size="56">
        <v-icon size="30">{{ style.icon }}</v-icon>
      </v-avatar>

      <div class="season-heading flex-grow-1">
        <p class="text-overline text-medium-emphasis mb-1">{{ t(`eventTypes.${event.eventType}`) }}</p>
        <h2 class="season-title text-h5 font-weight-bold mb-1">{{ eventTitle(event) }}</h2>
        <p class="text-body-2 text-medium-emphasis mb-0 d-flex align-center ga-1">
          <v-icon size="16">mdi-calendar-range</v-icon>
          <DateRange :from="event.dateStart" :to="event.dateEnd" />
        </p>
      </div>

      <v-chip
        :color="eventStateColor(event.state)"
        size="small"
        variant="flat"
        label
        class="flex-shrink-0"
      >
        {{ t(`eventStates.${event.state}`) }}
      </v-chip>
    </RouterLink>

    <v-divider />

    <v-card-text class="flex-grow-1 pa-4 pa-sm-5">
      <div class="d-flex flex-wrap align-center ga-2 mb-5">
        <v-chip
          v-if="event.allTracks"
          color="brown"
          size="small"
          variant="tonal"
          prepend-icon="mdi-earth"
        >
          {{ t('events.allTracks') }}
        </v-chip>

        <EventTracksBadge v-else :tracks="event.tracks" />

        <v-chip v-if="event.usesLaps" size="small" variant="tonal" prepend-icon="mdi-counter">
          {{ t('events.lapsCount', { n: event.defaultLaps }) }}
        </v-chip>
      </div>

      <!-- <div class="d-flex flex-wrap align-center ga-2 mb-2">
        <v-icon color="primary" size="20">mdi-format-list-numbered</v-icon>
        <h3 class="text-subtitle-1 font-weight-bold mb-0">{{ t('events.generalStandings') }}</h3>
        <v-chip size="small" variant="tonal" label>{{ t('events.topTen') }}</v-chip>
      </div> -->

      <div v-if="!topRows.length" class="text-center py-8">
        <v-icon size="48" color="medium-emphasis" class="mb-2">mdi-trophy-outline</v-icon>
        <p class="text-medium-emphasis mb-0">
          {{ event.playable ? t('events.noStandings') : t('events.notPlayable') }}
        </p>
      </div>

      <v-table v-else hover density="compact" class="season-table">
        <thead>
          <tr>
            <th class="text-center" style="width: 56px"></th>
            <th>{{ t('events.player') }}</th>
            <th class="text-center d-none d-sm-table-cell">{{ t('events.played') }}</th>
            <th class="text-center d-none d-sm-table-cell">{{ t('events.wins') }}</th>
            <th class="text-center">{{ t('events.points') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in topRows" :key="row.userId" :class="{ 'me-row': row.isMe }">
            <td class="text-center">
              <v-icon v-if="row.position <= 3" :class="podiumClass(row.position)" size="18">mdi-medal</v-icon>
              <span v-else class="text-medium-emphasis">{{ row.position }}</span>
            </td>
            <td class="font-weight-medium"><UserName :name="row.playerName" :crest="row.crest" /></td>
            <td class="text-center d-none d-sm-table-cell">
              <span v-if="row.played">{{ row.played }}</span>
              <span v-else class="text-medium-emphasis">–</span>
            </td>
            <td class="text-center d-none d-sm-table-cell">
              <span v-if="row.wins">{{ row.wins }}</span>
              <span v-else class="text-medium-emphasis">–</span>
            </td>
            <td class="text-center font-weight-bold">{{ row.points ?? '–' }}</td>
          </tr>
        </tbody>

        <!-- The player past the top ten keeps their row, in a body of its own so the separation
             line runs all the way across the table. -->
        <tbody v-if="myRowOutsideTop" class="pinned">
          <tr class="me-row">
            <td class="text-center text-medium-emphasis">{{ myRowOutsideTop.position }}</td>
            <td class="font-weight-medium">
              <UserName :name="myRowOutsideTop.playerName" :crest="myRowOutsideTop.crest" />
              <v-chip class="ms-2" color="primary" size="x-small" variant="tonal" label>
                {{ t('standings.yourPosition') }}
              </v-chip>
            </td>
            <td class="text-center d-none d-sm-table-cell">{{ myRowOutsideTop.played || '–' }}</td>
            <td class="text-center d-none d-sm-table-cell">{{ myRowOutsideTop.wins || '–' }}</td>
            <td class="text-center font-weight-bold">{{ myRowOutsideTop.points ?? '–' }}</td>
          </tr>
        </tbody>
      </v-table>
    </v-card-text>

    <v-divider class="mt-auto" />

    <div class="d-flex flex-wrap align-center ga-3 pa-4">
      <template v-if="event.myEntry">
        <v-icon color="primary" size="20">mdi-account-check</v-icon>
        <span class="text-caption text-medium-emphasis">{{ t('events.yourResult') }}</span>
        <v-chip
          :color="podiumColor(event.myEntry.position) ?? 'primary'"
          size="small"
          variant="flat"
          label
        >
          {{ t('events.position') }} {{ event.myEntry.position }}
        </v-chip>
        <span class="text-subtitle-1 font-weight-bold">
          {{ myResult }}
          <span v-if="hasPoints" class="text-caption font-weight-regular">{{ t('events.points') }}</span>
        </span>
      </template>

      <template v-else-if="event.playable">
        <v-icon size="20">mdi-clock-outline</v-icon>
        <span class="text-caption text-medium-emphasis">{{ t('events.todo') }}</span>
      </template>

      <template v-else>
        <v-icon size="20">mdi-hourglass-empty</v-icon>
        <span class="text-caption text-medium-emphasis">{{ t('events.notPlayable') }}</span>
      </template>

      <v-btn
        :to="eventLink"
        variant="text"
        size="small"
        color="primary"
        append-icon="mdi-chevron-right"
        class="ms-auto"
      >
        {{ t('events.openEvent') }}
      </v-btn>
    </div>
  </v-card>
</template>

<style scoped>
/* `v-ripple` is placed inside the element: it needs one that is positioned. */
.season-head {
  position: relative;
}

/* The title block shrinks below its content, so the state chip always keeps its place. */
.season-heading {
  min-width: 0;
}

.season-head:hover .season-title {
  text-decoration: underline;
}

/* The logged-in player stands out without breaking the row rhythm of the table. */
.me-row td {
  background-color: rgba(var(--v-theme-primary), 0.06);
}

.pinned td {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}
</style>
